import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { DelaStarIcon, HiringStatusBadge } from '~/modules/common/components';
import { showHiringStatusBadge } from '~/modules/common/components/HiringStatusBadge';
import { RoleName } from '~/modules/common/components/entities/role';
import {
  IconLinkButton,
  ResourceRequestStatusChip
} from '~/modules/resourcing/common/components';
import { ResourceRequestStatus } from '~/types';
import { useMeContext } from '~/modules/me';
import { useIsPtiEnabled } from '~/modules/common/hooks';
import ResourceRequestAllocations from '../../ResourceRequestAllocations';
import ResourceRequestResourceInfo from '../../ResourceRequestResourceInfo';
import {
  useStyles,
  useStatusChipStyle,
  useDelaStyles,
  useDelaStarIconStyles,
  useDelaStarIconStyles2,
  useHiringBadgeStyles
} from './useStyles';

export const ResourceRequestRowDetailsContent1 = ({
  allocations,
  resourceRequest,
  showResourceAllocation,
  selectedRole,
  maxSkillLevel,
  isPsaPraaResourcingUIAndRMRole,
  requisitionHiringStatus
}) => {
  const {
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();

  const isPtiEnabled = useIsPtiEnabled();

  const classes = useStyles();
  const statusChipClasses = useStatusChipStyle();
  const delaClasses = useDelaStyles();
  const delaStarIconClasses = useDelaStarIconStyles();
  const delaStarIconClasses2 = useDelaStarIconStyles2();
  const hiringBadgeClasses = useHiringBadgeStyles();

  const shouldUseDelaColumn =
    isPsaPraaResourcingUIAndRMRole &&
    resourceRequest.requestStatus === ResourceRequestStatus.Submitted;
  const showAllocations = isPsaPraaResourceMatchingEnabled
    ? (resourceRequest.requestStatus !== ResourceRequestStatus.Submitted ||
        !isPsaPraaResourcingUIAndRMRole) &&
      showResourceAllocation
    : showResourceAllocation;
  const showResourceRequestInfo = isPsaPraaResourceMatchingEnabled
    ? (resourceRequest.requestStatus === ResourceRequestStatus.Draft ||
        (resourceRequest.requestStatus === ResourceRequestStatus.Submitted &&
          !isPsaPraaResourcingUIAndRMRole)) &&
      allocations.length === 0
    : (resourceRequest.requestStatus === ResourceRequestStatus.Draft ||
        resourceRequest.requestStatus === ResourceRequestStatus.Submitted) &&
      allocations.length === 0;

  const iconLinkButtonClasses = useMemo(
    () => ({ openButton: classes.openButton }),
    [classes.openButton]
  );

  return (
    <div
      className={classNames(classes.root, {
        [delaClasses.root]:
          !isPsaPraaResourceMatchingEnabled &&
          resourceRequest.isDelaDraftResourceRequest
      })}
    >
      <div className={classNames(classes.column, classes.column2)}>
        <div className={classes.roleColumn}>
          {selectedRole ? (
            <RoleName role={selectedRole} className={classes.roleName} />
          ) : (
            <span className={classes.noValue}>
              <FormattedMessage id="resourceRequestReadonlyItem.noRole" />
            </span>
          )}
          {isPtiEnabled &&
            showHiringStatusBadge({
              resourceRequest
            }) && (
              <HiringStatusBadge
                requisitionHiringStatus={requisitionHiringStatus}
                classes={hiringBadgeClasses}
              />
            )}
        </div>
        <div
          className={classNames(
            isPsaPraaResourceMatchingEnabled
              ? classes.outerColumn2
              : classes.outerColumn,
            {
              [classes.delaColumn]:
                !isPsaPraaResourceMatchingEnabled && shouldUseDelaColumn
            }
          )}
        >
          {(!isPsaPraaResourceMatchingEnabled || showAllocations) && (
            <div className={classes.column}>
              {showResourceRequestInfo && (
                <ResourceRequestResourceInfo
                  resourceRequest={resourceRequest}
                  maxSkillLevel={maxSkillLevel}
                  isPsaPraaResourceMatchingEnabled={
                    isPsaPraaResourceMatchingEnabled
                  }
                />
              )}
              {isPsaPraaResourceMatchingEnabled &&
                resourceRequest.isDelaDraftResourceRequest && (
                  <DelaStarIcon classes={delaStarIconClasses2} />
                )}
              {showAllocations && (
                <ResourceRequestAllocations
                  allocations={allocations}
                  showResourceAllocation={showResourceAllocation}
                  requestStatus={resourceRequest.requestStatus}
                  preferredResources={resourceRequest?.preferredResources}
                />
              )}
            </div>
          )}
          <div
            className={classNames(classes.statusColumn, {
              [classes.delaColumn]:
                !isPsaPraaResourceMatchingEnabled && shouldUseDelaColumn
            })}
          >
            {!(
              isPsaPraaResourcingUIAndRMRole &&
              resourceRequest.requestStatus === ResourceRequestStatus.Submitted
            ) && (
              <>
                {!isPsaPraaResourceMatchingEnabled &&
                  resourceRequest.isDelaDraftResourceRequest && (
                    <DelaStarIcon classes={delaStarIconClasses} />
                  )}
                <ResourceRequestStatusChip
                  status={resourceRequest.requestStatus}
                  classes={statusChipClasses}
                />
              </>
            )}
            <div>
              <IconLinkButton
                classes={iconLinkButtonClasses}
                qeIdTag="Drawer_Icon"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ResourceRequestRowDetailsContent1.propTypes = {
  resourceRequest: PropTypes.object.isRequired,
  allocations: PropTypes.array,
  showResourceAllocation: PropTypes.bool,
  selectedRole: PropTypes.object,
  maxSkillLevel: PropTypes.number,
  isPsaPraaResourcingUIAndRMRole: PropTypes.bool,
  requisitionHiringStatus: PropTypes.string
};

export default ResourceRequestRowDetailsContent1;
